import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  type TemplateResult,
} from '@design.estate/dees-element';

import '@design.estate/dees-catalog';
import { dotglobalSharedStyles } from './dotglobal-shared-styles.js';

@customElement('dotglobal-settings')
export class DotglobalSettings extends DeesElement {
  @property({ type: String })
  public accessor user = '';

  @property({ type: String })
  public accessor displayName = '';

  @property({ type: String })
  public accessor absoluteExpiresAt = '';

  public static styles = [
    cssManager.defaultStyles,
    dotglobalSharedStyles,
    css`
      :host { display: block; }
      .settings-grid {
        display: grid;
        grid-template-columns: minmax(0, 1fr) minmax(280px, 0.72fr);
        gap: 16px;
        align-items: start;
      }
      .fact {
        display: grid;
        grid-template-columns: 150px minmax(0, 1fr);
        gap: 14px;
        padding: 12px 0;
        border-bottom: 1px solid var(--border-subtle);
        font-size: 13px;
      }
      .fact:last-child { border-bottom: 0; }
      .fact-label { color: var(--text-muted); font-size: 12px; }
      .session-mark {
        display: grid;
        place-items: center;
        width: 42px;
        height: 42px;
        border-radius: 10px;
        background: color-mix(in srgb, var(--success) 12%, transparent);
        color: var(--success);
      }
      @media (max-width: 760px) {
        .settings-grid { grid-template-columns: 1fr; }
        .fact { grid-template-columns: 1fr; gap: 4px; }
      }
    `,
  ];

  public render(): TemplateResult {
    return html`
      <div class="page content" style="max-width: 980px">
        <div class="row gap12" style="align-items: center; margin-bottom: 22px">
          ${this.renderAvatar(this.user, 44)}
          <div>
            <h1 class="sectitle" style="font-size: 21px; margin: 0">Account</h1>
            <div class="muted" style="font-size: 12.5px">Current server-backed foss.global session</div>
          </div>
        </div>

        <div class="settings-grid">
          <div class="card setcard">
            <div class="card-h">${this.icon('User', 'var(--accent)')}<span class="grow">Native account</span></div>
            <div class="setbody">
              <div class="fact">
                <span class="fact-label">Username</span>
                <b class="mono">${this.user}</b>
              </div>
              <div class="fact">
                <span class="fact-label">Display name</span>
                <span>${this.displayName || this.user}</span>
              </div>
              <div class="fact">
                <span class="fact-label">Sign-in identity</span>
                <span>Bound by a global administrator through idp.global</span>
              </div>
              <div class="muted" style="font-size: 12px; line-height: 1.55; margin-top: 14px">
                Profile editing and self-service identity linking are not exposed until their server-side authorization and persistence contracts exist.
              </div>
            </div>
          </div>

          <div class="stack gap16">
            <div class="card setcard">
              <div class="card-h">${this.icon('Monitor', 'var(--success)')}<span class="grow">Current session</span></div>
              <div class="setbody stack gap14">
                <div class="row gap12" style="align-items: center">
                  <span class="session-mark">${this.icon('ShieldCheck')}</span>
                  <div>
                    <b style="font-size: 13.5px">This browser</b>
                    <div class="muted" style="font-size: 11.5px; margin-top: 2px">Secure HttpOnly cookie · active now</div>
                  </div>
                </div>
                <div class="fact">
                  <span class="fact-label">Absolute expiry</span>
                  <span>${this.expiryLabel()}</span>
                </div>
                <button class="btn danger" @click=${() => this.signOut()}>${this.icon('LogOut')} Sign out this browser</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    `;
  }

  private expiryLabel(): string {
    const timestamp = Date.parse(this.absoluteExpiresAt);
    return Number.isFinite(timestamp)
      ? new Date(timestamp).toLocaleString()
      : 'Unavailable';
  }

  private signOut(): void {
    this.dispatchEvent(new CustomEvent('dotglobal-signout', {
      bubbles: true,
      composed: true,
    }));
  }

  private icon(nameArg: string, colorArg = ''): TemplateResult {
    return html`<dees-icon style=${colorArg ? `color:${colorArg}` : ''} .icon=${`lucide:${nameArg}`} .iconSize=${15}></dees-icon>`;
  }

  private renderAvatar(labelArg: string, sizeArg: number): TemplateResult {
    const initials = labelArg
      .split(/[.\s/-]+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((partArg) => partArg[0]?.toUpperCase())
      .join('') || 'fg';
    return html`<span class="avatar" style=${`width:${sizeArg}px; height:${sizeArg}px; font-size:${Math.round(sizeArg * 0.38)}px`}>${initials}</span>`;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'dotglobal-settings': DotglobalSettings;
  }
}
